import { useState, useContext, createContext } from "react";
import api from "../services/api";
import * as Sentry from "@sentry/react";
import { useVisibility } from "./useVisibility";

export const authContext = createContext();

function useAuthProvider() {
  const [token, setToken] = useState();
  const [user, setUser] = useState();
  const [showNetworkError, setShowNetworkError] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const { setPinnedTabs, setVisibilityMap } = useVisibility();
  const [employeeNames, setEmployeeNames] = useState([]);

  const privilegeLabels = {
    admin: {
      admin: {
        label: "Admin",
        description: "Full administrative access to all system features and settings.",
      },
    },
    settings: {
      editRoles: {
        label: "Edit Roles",
        description: "Can create, update, or delete user roles and their associated privileges.",
      },
    },
  };

  // Flatten privilegeLabels to a map: { privilegeKey: { label, description } }
  const privilegeLabelsMap = Object.values(privilegeLabels).reduce((acc, curr) => {
    Object.entries(curr).forEach(([key, value]) => {
      acc[key] = value;
    });
    return acc;
  }, {});

  const [domainsTimestamp, setDomainsTimestamp] = useState(Date.now());

  const login = async (credentialResponse) => {
    let result = await api.login(credentialResponse);

    if (result.status === 200) {
      checkAuth();
      return true;
    } else {
      if (result.problem === "NETWORK_ERROR") {
        setShowNetworkError(true);
      }
      return false;
    }
  };

  const logout = async () => {
    await api.logout();

    setToken(null);
    setUser(null);
  };

  const checkAuth = async () => {
    setIsConnecting(true);
    const response = await api.validateToken();

    if (response.status === 200 && response.data) {
      setUser(response.data);
      Sentry.setUser({
        email: response.data.email,
        id: response.data.id,
      });

      api.getUserNames().then((res) => {
        if (res.ok) setEmployeeNames(res.data || []);
      });

      if (response.data.pinnedTabs && Array.isArray(response.data.pinnedTabs)) {
        setPinnedTabs(response.data.pinnedTabs);
      }

      setVisibilityMap(response.data.visibility || {});
    } else {
      if (response.problem === "NETWORK_ERROR") {
        setShowNetworkError(true);
      }
    }
    setIsConnecting(false);
  };

  const checkPrivileges = (action) => {
    let privileges = user?.roles?.reduce((acc, role) => {
      if (role.privileges && Array.isArray(role.privileges)) {
        acc.push(...role.privileges);
      }
      return acc;
    }, []);

    if (privileges?.indexOf("admin") !== -1) {
      return true;
    }

    return privileges?.indexOf(action) !== -1;
  };

  return {
    token,
    user,
    setToken,
    checkAuth,
    logout,
    login,
    checkPrivileges,
    privilegeLabels,
    privilegeLabelsMap,
    showNetworkError,
    setShowNetworkError,
    isConnecting,
    domainsTimestamp,
    setDomainsTimestamp,
    employeeNames,
  };
}

export function ProvideAuth({ children }) {
  return <authContext.Provider value={useAuthProvider()}>{children}</authContext.Provider>;
}

export const useAuth = () => {
  return useContext(authContext);
};
